import { create } from 'zustand'
import { FileType } from '../types'
import { createJSONStorage, persist } from 'zustand/middleware'

const MAX_FILES = 8

type FileItem = {
  name: string
  type: FileType
  url?: string
  instance: File
  status: 'loading' | 'error' | 'uploaded'
}

type ComposerFileStore = {
  files: FileItem[]
  setFiles: (files: FileItem[]) => void
  upsertFile: (file: FileItem) => void
  uploadFile: (
    file: File,
    uploadFile: (file: File) => Promise<{ fileUrl: string; name: string; type: FileType }>
  ) => void
  deleteFile: (filename: string) => void
}

const createComposerFileStore = (name: string) =>
  create<ComposerFileStore>()(
    persist(
      (set, get) => ({
        files: [],
        setFiles: (files) => set({ files }),
        upsertFile: (incomingFile) =>
          set(({ files }) => {
            const fileExists = files.some((f) => f.name === incomingFile.name)

            const filesArr = fileExists
              ? files.map((file) => (file.name === incomingFile.name ? { ...file, ...incomingFile } : file))
              : [...files, { ...incomingFile }]

            return { files: filesArr }
          }),
        uploadFile: async (file, uploadFile) => {
          const isUpdate = get().files.some((f) => f.name === file.name)
          const hasReachedMaxFiles = get().files.length === MAX_FILES

          if (!isUpdate && hasReachedMaxFiles) return

          get().upsertFile({
            name: file.name,
            type: 'file',
            instance: file,
            status: 'loading',
          })
          try {
            const { fileUrl: url, type } = await uploadFile(file)

            get().upsertFile({
              name: file.name,
              type: type,
              url: url,
              instance: file,
              status: 'uploaded',
            })
          } catch (error) {
            console.error('Error sending file:', error)

            get().upsertFile({
              name: file.name,
              type: 'file',
              instance: file,
              status: 'error',
            })
          }
        },
        deleteFile: (filename) =>
          set(({ files }) => {
            return { files: files.filter((file) => file.name != filename) }
          }),
      }),
      {
        name: name,
        storage: createJSONStorage(() => sessionStorage),
      }
    )
  )

type BoundComposerFileStore = ReturnType<typeof createComposerFileStore>
class ComposerFileStores {
  private static _instances: { [key: string]: BoundComposerFileStore } = {}
  private constructor() {}

  public static getInstance(name = 'bp-composer-file-store'): BoundComposerFileStore {
    if (!ComposerFileStores._instances[name]) {
      ComposerFileStores._instances[name] = createComposerFileStore(name)
    }
    return ComposerFileStores._instances[name]
  }
}

export const getComposerFileStore = ComposerFileStores.getInstance
